import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { MaterialReactTable } from "material-react-table";
import { amsPlantListColumns } from "./columns";
import { Box, Tab, Tabs } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { amsTabs } from "./AmsTabs/ApsTabsConfig";
import { AMS_TAB_LABEL, getHash, setHash } from "./AmsTabs/hashFunctions";
import { secondAxios } from "../../../api/axios";
import { useAuthContext } from "../../../context/authContext";
import { AmsModuleDataProvider } from "./context/AmsModuleDataContext";

/**
 * Relative column importance.
 * These are weights — NOT pixel sizes.
 *
 * The final pixel width is recalculated whenever the browser/container
 * width changes.
 */
const AMS_COLUMN_WEIGHTS = Object.freeze({
  name: 1.8,
  clientName: 1.05,
  people: 2.1,
  projects: 0.9,

  ditIntelligentPlantData: 0.75,
  realtimePlantData: 0.75,
  ditMaintenanceSystem: 0.75,
});

const AMS_TABLE_HEADER_FONT_SIZE = "clamp(0.5rem, 0.72vw, 0.82rem)";

const AMS_TABLE_BODY_FONT_SIZE = "clamp(0.5rem, 0.9vw, 0.95rem)";

const AMS_TABLE_HEADER_FONT_WEIGHT = 700;
const AMS_TABLE_BODY_FONT_WEIGHT = 500;

const amsTableHeadFontSx = {
  fontSize: `${AMS_TABLE_HEADER_FONT_SIZE} !important`,
  fontWeight: `${AMS_TABLE_HEADER_FONT_WEIGHT} !important`,
  fontFamily: "inherit",
  fontStyle: "normal",
  lineHeight: "1.05 !important",
};

const amsTableBodyFontSx = {
  fontSize: `${AMS_TABLE_BODY_FONT_SIZE} !important`,
  fontWeight: `${AMS_TABLE_BODY_FONT_WEIGHT} !important`,
  fontFamily: "inherit",
  fontStyle: "normal",
  lineHeight: "1.2 !important",
};

const getAmsColumnId = (column) =>
  String(column?.id || column?.accessorKey || "");

const getAmsColumnWeight = (column) => {
  const id = getAmsColumnId(column);

  return AMS_COLUMN_WEIGHTS[id] ?? 1;
};

/**
 * Force MRT/TanStack to use the exact responsive width calculated
 * from the currently visible browser area.
 */
const applyResponsiveAmsColumnWidth = (column, widthPx) => {
  const safeWidth = Math.max(1, widthPx);

  const currentHeadProps =
    column?.muiTableHeadCellProps &&
    typeof column.muiTableHeadCellProps === "object"
      ? column.muiTableHeadCellProps
      : {};

  const currentBodyProps =
    column?.muiTableBodyCellProps &&
    typeof column.muiTableBodyCellProps === "object"
      ? column.muiTableBodyCellProps
      : {};

  return {
    ...column,

    size: safeWidth,
    minSize: safeWidth,
    maxSize: safeWidth,
    grow: false,

    muiTableHeadCellProps: {
      ...currentHeadProps,

      sx: {
        ...(currentHeadProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        overflow: "hidden",

        ...amsTableHeadFontSx,
      },
    },

    muiTableBodyCellProps: {
      ...currentBodyProps,

      sx: {
        ...(currentBodyProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        overflow: "hidden",

        ...amsTableBodyFontSx,

        "& .Mui-TableBodyCell-Content-Wrapper": {
          ...(currentBodyProps.sx?.["& .Mui-TableBodyCell-Content-Wrapper"] ||
            {}),

          width: "100%",
          minWidth: 0,
          maxWidth: "100%",

          overflow: "hidden",

          ...amsTableBodyFontSx,
        },
      },
    },
  };
};

function AmsProjectsTable() {
  const tableViewportRef = useRef(null);

  const [tableViewportWidth, setTableViewportWidth] = useState(() => {
    if (typeof window === "undefined") return 1200;

    return document.documentElement.clientWidth;
  });

  useLayoutEffect(() => {
    let frameId = null;

    const updateTableViewportWidth = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const element = tableViewportRef.current;

        if (!element) return;

        const rect = element.getBoundingClientRect();
        const viewportRight = document.documentElement.clientWidth;

        const parent = element.parentElement;
        const parentRect = parent?.getBoundingClientRect();

        let rightEdge = viewportRight;

        if (parentRect) {
          const parentStyles = window.getComputedStyle(parent);

          const parentPaddingRight = parseFloat(parentStyles.paddingRight) || 0;

          rightEdge = Math.min(
            viewportRight,
            parentRect.right - parentPaddingRight,
          );
        }

        const availableWidth = Math.max(
          1,
          Math.floor(rightEdge - Math.max(rect.left, 0) - 1),
        );

        setTableViewportWidth((previous) =>
          previous === availableWidth ? previous : availableWidth,
        );
      });
    };

    updateTableViewportWidth();

    window.addEventListener("resize", updateTableViewportWidth);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateTableViewportWidth)
        : null;

    if (resizeObserver && tableViewportRef.current?.parentElement) {
      resizeObserver.observe(tableViewportRef.current.parentElement);
    }

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      window.removeEventListener("resize", updateTableViewportWidth);

      resizeObserver?.disconnect();
    };
  }, []);

  const [plants, setPlants] = useState([]);
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
  });

  const { user } = useAuthContext();
  const navigate = useNavigate();

  const responsiveColumns = useMemo(() => {
    const totalWeight = amsPlantListColumns.reduce(
      (total, column) => total + getAmsColumnWeight(column),
      0,
    );

    /*
     * Tiny safety offset prevents borders/sub-pixel rounding
     * from creating a horizontal scrollbar.
     */
    const usableWidth = Math.max(1, tableViewportWidth - 2);

    return amsPlantListColumns.map((column) => {
      const weight = getAmsColumnWeight(column);

      const widthPx = (weight / totalWeight) * usableWidth;

      return applyResponsiveAmsColumnWidth(column, widthPx);
    });
  }, [tableViewportWidth]);

  React.useEffect(() => {
    if (!user?.client) {
      return;
    }

    async function fetchPlants() {
      try {
        setState((prev) => ({ ...prev, isLoading: true, isError: false }));

        const res = await secondAxios.get("/plant");
        const clientName = process.env.REACT_APP_CLIENT_NAME;
        console.log("res", res.data);
        const filtered =
          user.client === clientName
            ? res.data.filter((plant) => plant.aimsSent === true)
            : Array.isArray(res.data)
              ? res.data.filter(
                  (plant) =>
                    plant.clientName === user.client && plant.aimsSent === true,
                )
              : [];
        console.log("filtered", filtered);

        if (filtered.length === 1) {
          navigate(`/ams/${filtered[0].id}`, { replace: true });
          return;
        }

        setPlants(filtered);
      } catch (err) {
        console.error("Error fetching plants for AMS list:", err);
        setState((prev) => ({ ...prev, isError: true }));
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    }

    fetchPlants();
  }, [user?.client]);

  return (
    <Box
      sx={{
        p: 2,

        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,

        boxSizing: "border-box",

        overflowX: "hidden",
      }}
    >
      <Box
        ref={tableViewportRef}
        sx={{
          width: `${tableViewportWidth}px`,
          minWidth: 0,
          maxWidth: `${tableViewportWidth}px`,

          boxSizing: "border-box",

          overflowX: "hidden",
          overflowY: "visible",
        }}
      >
        <MaterialReactTable
          data={plants}
          columns={responsiveColumns}
          layoutMode="grid-no-grow"
          defaultColumn={{
            size: 1,
            minSize: 1,
            maxSize: 100000,
            grow: false,
          }}
          enableEditing={false}
          enableFacetedValues
          initialState={{
            showColumnFilters: true,
          }}
          muiTableProps={{
            sx: {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden !important",
            },
          }}
          muiTablePaperProps={{
            elevation: 0,

            sx: {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              boxSizing: "border-box",

              overflowX: "hidden !important",
            },
          }}
          muiTableHeadRowProps={{
            sx: {
              display: "flex !important",

              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden",

              alignItems: "stretch",

              "& .MuiTableCell-root": {
                boxSizing: "border-box",
                overflow: "hidden",
                verticalAlign: "middle",
              },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              boxSizing: "border-box",
              overflow: "hidden",

              textAlign: "center",
              verticalAlign: "middle",

              px: {
                xs: 0,
                sm: 0.15,
                md: 0.4,
                lg: 0.6,
              },

              borderRight: "1px solid rgba(224, 224, 224, 1)",

              ...amsTableHeadFontSx,

              "& .Mui-TableHeadCell-Content": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                justifyContent: "center",
                overflow: "hidden",

                ...amsTableHeadFontSx,
              },

              "& .Mui-TableHeadCell-Content-Labels": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                justifyContent: "center",
                overflow: "hidden",

                ...amsTableHeadFontSx,
              },

              "& .Mui-TableHeadCell-Content-Wrapper": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                whiteSpace: "normal !important",
                overflowWrap: "anywhere",
                wordBreak: "break-word",

                overflow: "hidden !important",

                textAlign: "center",

                ...amsTableHeadFontSx,
              },
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              boxSizing: "border-box",

              minWidth: "0 !important",
              overflow: "hidden",

              px: {
                xs: 0,
                sm: 0.15,
                md: 0.4,
                lg: 0.6,
              },

              borderRight: "1px solid rgba(224, 224, 224, 1)",

              ...amsTableBodyFontSx,

              "& .Mui-TableBodyCell-Content-Wrapper": {
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",

                overflow: "hidden",

                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",

                ...amsTableBodyFontSx,
              },

              "& .Mui-TableBodyCell-Content-Wrapper > span": {
                fontSize: `${AMS_TABLE_BODY_FONT_SIZE} !important`,
                fontWeight: `${AMS_TABLE_BODY_FONT_WEIGHT} !important`,
              },
            },
          }}
          muiTableContainerProps={{
            sx: {
              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              maxHeight: "none",

              /*
               * NEVER create horizontal scrolling.
               */
              overflowX: "hidden !important",

              "& .MuiTable-root": {
                width: `${tableViewportWidth}px !important`,
                minWidth: `${tableViewportWidth}px !important`,
                maxWidth: `${tableViewportWidth}px !important`,
              },

              "& .MuiTableHead-root, & .MuiTableBody-root": {
                width: `${tableViewportWidth}px !important`,
                minWidth: `${tableViewportWidth}px !important`,
                maxWidth: `${tableViewportWidth}px !important`,

                overflow: "hidden",
              },

              "& .MuiTableRow-root": {
                width: `${tableViewportWidth}px !important`,
                minWidth: `${tableViewportWidth}px !important`,
                maxWidth: `${tableViewportWidth}px !important`,

                overflow: "hidden",
              },

              "& .MuiTableCell-root": {
                boxSizing: "border-box",
                overflow: "hidden",
              },

              /*
               * Do not let filters create a minimum column width.
               */
              "& .MuiTextField-root, & .MuiFormControl-root": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                overflow: "hidden",
              },

              "& .MuiInputBase-root": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                padding: "0 !important",

                overflow: "hidden",
              },

              "& .MuiInputBase-input": {
                width: "100%",
                minWidth: "0 !important",
                maxWidth: "100%",

                padding: {
                  xs: "0 !important",
                  sm: "1px !important",
                  md: "3px !important",
                },

                fontSize: `${AMS_TABLE_BODY_FONT_SIZE} !important`,
                fontWeight: `${AMS_TABLE_BODY_FONT_WEIGHT} !important`,
              },

              /*
               * Responsive action icons.
               */
              "& .MuiTableBody-root .MuiSvgIcon-root": {
                fontSize: "clamp(0.65rem, 1vw, 1.15rem) !important",
              },

              "& .MuiIconButton-root": {
                minWidth: "0 !important",

                padding: {
                  xs: "0px",
                  sm: "1px",
                  md: "3px",
                },
              },
            },
          }}
          muiToolbarAlertBannerProps={
            state.isError
              ? {
                  color: "error",
                  children: "Error loading plant data",
                }
              : undefined
          }
          state={{
            isLoading: state.isLoading,
            showAlertBanner: state.isError,
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: (event) => {
              const target = event.target;

              if (
                !target.closest(".MuiButtonBase-root") &&
                !target.closest("a")
              ) {
                navigate(`/ams/${row.original.id}`);
              }
            },

            sx: {
              display: "flex !important",

              width: `${tableViewportWidth}px !important`,
              minWidth: `${tableViewportWidth}px !important`,
              maxWidth: `${tableViewportWidth}px !important`,

              overflow: "hidden",

              cursor: "pointer",

              "& .MuiTableCell-root": {
                boxSizing: "border-box",
                overflow: "hidden",
              },
            },
          })}
        />
      </Box>
    </Box>
  );
}

function getCurrentTabIndex(tabs) {
  const hashValue = getHash(AMS_TAB_LABEL);
  const tabIndex = tabs.findIndex((tab) => tab.title === hashValue);

  if (tabIndex !== -1) {
    return tabIndex;
  }

  setHash(AMS_TAB_LABEL, tabs[0].title);
  return 0;
}

function AmsProjectTabs({ projectId }) {
  const [, setRerenderKey] = useState(Math.random());

  const reRender = useCallback(() => {
    setRerenderKey(Math.random());
  }, []);

  const tabs = useMemo(() => {
    return amsTabs(reRender, projectId);
  }, [reRender, projectId]);

  const [tabState, setTabState] = useState(() => {
    const initialTab = getCurrentTabIndex(tabs);

    return {
      currentTab: initialTab,
      mountedTabs: {
        [initialTab]: true,
      },
    };
  });

  function handleChange(_, newValue) {
    setHash(AMS_TAB_LABEL, tabs[newValue].title);

    setTabState((prev) => ({
      currentTab: newValue,
      mountedTabs: {
        ...prev.mountedTabs,
        [newValue]: true,
      },
    }));
  }

  const { currentTab, mountedTabs } = tabState;

  return (
    <AmsModuleDataProvider plantId={projectId}>
      <Box flex={1}>
        <Tabs value={currentTab} onChange={handleChange}>
          {tabs.map((tab, index) => (
            <Tab
              key={tab.title}
              label={tab.title}
              sx={{
                minWidth: 200,
                maxWidth: 320,
                whiteSpace: "normal",
                textAlign: "center",
                py: 1,
              }}
            />
          ))}
        </Tabs>

        <Box>
          {tabs.map((tab, index) => {
            if (!mountedTabs[index]) {
              return null;
            }

            return (
              <Box
                key={tab.title}
                sx={{
                  display: currentTab === index ? "block" : "none",
                }}
              >
                {tab.panel}
              </Box>
            );
          })}
        </Box>
      </Box>
    </AmsModuleDataProvider>
  );
}

export function AmsPage() {
  const { id: projectId } = useParams();

  if (projectId) {
    return <AmsProjectTabs key={projectId} projectId={projectId} />;
  }

  return <AmsProjectsTable />;
}
